/**
 * Accounts & Net Worth Visualization
 *
 * Shows:
 * - Net worth trend over time (area chart from statement balances)
 * - Account groups (Cash, Credit Cards, Loans, Investments) with sparklines,
 *   split into Personal and per business / other entity when any account is
 *   titled to one (see utils/accountAssignment.js)
 * - Summary panel with asset/liability totals
 *
 * All data comes from the /account/summary API endpoint.
 * No hardcoded values — everything is derived from real case data.
 */

import React, { useMemo, useState } from 'react';
import { useGetAccountSummaries } from '../api/bankaccounts';
import { updateAccount } from '../api/accounts';
import { useIsSuperAdmin } from '../api/swrcurrentusercontext';
import AccountIdentityCorrectionDialog from '../components/ui/AccountIdentityCorrectionDialog';
import AccountMergeDialog from '../components/ui/AccountMergeDialog';
import AccountTitleEditDialog from '../components/ui/AccountTitleEditDialog';
import AccountLedgerDialog, { accountLedgerLabel } from '../components/ui/AccountLedgerDialog';
import ReorderAccountsDialog from '../components/ui/ReorderAccountsDialog';
import usePrintAccountsNetWorthPDF from '../components/ui/PDFPrintAccountsNetWorth';
import { PictureAsPdf as PictureAsPdfIcon, SwapVert as SwapVertIcon } from '@mui/icons-material';
import { useGetCaseStats } from '../api/casestats';
import { useGetEnrichedTransactions, flattenInfiniteData } from '../api/transactions';
import { formatCurrency, formatQuantity, formatAccountType, formatOwnershipAllocation } from '../utils/formatters';
import { groupByAssignment, hasEntityAssignments, PERSONAL_ASSIGNMENT_KEY, ENTITY_ASSIGNMENTS_LABEL } from '../utils/accountAssignment';
import { PageHeader, SectionTitle } from '../design-system';
import NetWorthChart from '../components/charts/NetWorthChart';
import CashFlowChart from '../components/charts/CashFlowChart';
import ReportCategorySankeyAnalysis, { perspectiveMap, tiersForPerspective } from '../components/ui/report-sankey-analysis';
import styles from './page-accounts-net-worth.module.css';

// SWR key useGetAccountSummaries reads from -- kept in sync with bankaccounts.js
// (mirrors the same local-constant pattern AccountTitleEditDialog already uses
// for its own default cache key).
const ACCOUNT_SUMMARIES_CACHE_KEY = 'ACCOUNT_SUMMARIES';

/* ---- Account Type Classification ---- */
const ACCOUNT_GROUPS = {
  cash: {
    label: 'Cash',
    types: ['checking', 'savings', 'money market', 'cash'],
    iconClass: 'accountIconBank',
    dotColor: 'var(--color-info-500)',
    sparkColor: 'var(--color-accent-500)',
  },
  credit: {
    label: 'Credit Cards',
    types: ['credit', 'credit card', 'line of credit'],
    iconClass: 'accountIconCredit',
    dotColor: 'var(--color-warning-500)',
    sparkColor: 'var(--color-warning-500)',
    isLiability: true,
  },
  loan: {
    label: 'Loans & Mortgages',
    types: ['loan', 'mortgage', 'heloc'],
    iconClass: 'accountIconCredit',
    dotColor: 'var(--color-error-500)',
    sparkColor: 'var(--color-error-500)',
    isLiability: true,
  },
  investment: {
    label: 'Investments',
    types: ['investment', 'brokerage', 'retirement', '401k', 'ira', 'roth', '529', 'utma', 'custodian'],
    iconClass: 'accountIconInvest',
    dotColor: 'var(--color-accent-500)',
    sparkColor: 'var(--color-success-500)',
  },
  other: {
    label: 'Other Accounts',
    types: [],
    iconClass: 'accountIconBank',
    dotColor: 'var(--color-gray-400)',
    sparkColor: 'var(--color-gray-500)',
  },
};

function classifyAccount(account) {
  // Fold the enum code to space-separated lowercase so canonical codes match
  // the group vocabulary below: "MONEY_MARKET" -> "money market" (cash),
  // "LINE_OF_CREDIT" -> "line of credit" (credit), "ROLLOVER_IRA" ->
  // "rollover ira" (investment). Without the underscore fold, MONEY_MARKET
  // fell through to "Other Accounts".
  const type = (account.account_type || '').toLowerCase().replace(/_/g, ' ');
  // Asset vs. liability follows the account's `_asset` flag -- the same field
  // the MBS uses, set at publish from LIABILITY_ACCOUNT_TYPES -- so a loan /
  // mortgage / HELOC is never counted as an asset here. Accounts without the
  // flag fall back to the type vocabulary alone.
  const isLiability = account._asset === false;
  const isAsset = account._asset === true;
  // The backend's canonical group (account_grouping.py, the same one the
  // Quality & Completeness report uses) recognizes every investment type by
  // its enum code -- e.g. TAXABLE -> "Investment (Taxable)" -- where the word
  // match below would miss it and fall through to "Other Accounts".
  if (!isLiability && (account.account_group || '').startsWith('Investment')) return 'investment';
  if (!isAsset && account.account_group === 'Loan / Mortgage') return 'loan';
  for (const [groupKey, group] of Object.entries(ACCOUNT_GROUPS)) {
    if (groupKey === 'other') continue;
    if (isAsset && group.isLiability) continue;
    if (isLiability && !group.isLiability) continue;
    if (group.types.some((t) => type.includes(t))) {
      return groupKey;
    }
  }
  return isLiability ? 'loan' : 'other';
}

// Bucket accounts by type, summing balances per bucket.
function groupByType(accounts) {
  const groups = {};
  for (const account of accounts) {
    const groupKey = classifyAccount(account);
    if (!groups[groupKey]) {
      groups[groupKey] = {
        ...ACCOUNT_GROUPS[groupKey],
        key: groupKey,
        accounts: [],
        total: 0,
        totalChange: 0,
      };
    }
    groups[groupKey].accounts.push(account);
    groups[groupKey].total += account.latest_balance ?? 0;
    groups[groupKey].totalChange += account.balance_change ?? 0;
  }
  return groups;
}

// Net of a type-bucket map: assets minus the absolute liabilities.
function netOfGroups(grouped) {
  return Object.values(grouped).reduce(
    (sum, g) => (g.isLiability ? sum - Math.abs(g.total) : sum + g.total), 0);
}

const GROUP_ORDER = ['cash', 'credit', 'loan', 'investment', 'other'];


/* ---- Format Helpers ---- */
function formatDollar(num) {
  if (num == null) return '$0.00';
  return `$${formatCurrency(num)}`;
}

function formatChangeStr(change, pct) {
  if (change == null) return null;
  const sign = change >= 0 ? '+' : '';
  const pctStr = pct != null ? ` (${Math.abs(pct).toFixed(1)}%)` : '';
  return `${sign}$${formatCurrency(change)}${pctStr}`;
}

/* ---- Sparkline SVG ---- */
function Sparkline({ balanceHistory, color = 'var(--color-accent-500)', width = 60, height = 24 }) {
  if (!balanceHistory || balanceHistory.length < 2) return null;

  const balances = balanceHistory.map((p) => p.balance ?? 0);
  const min = Math.min(...balances);
  const max = Math.max(...balances);
  const range = max - min || 1;

  const points = balances.map((val, i) => {
    const x = (i / (balances.length - 1)) * width;
    const y = height - 2 - ((val - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Balance trend">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}


/* ---- Account Icons ---- */
function BankIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" /><path d="M3 10h18" /><path d="M12 3l9 7H3z" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}

function InvestmentIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22,7 13.5,15.5 8.5,10.5 2,17" /><polyline points="16,7 22,7 22,13" />
    </svg>
  );
}

function getAccountIcon(groupKey) {
  switch (groupKey) {
    case 'credit': return <CreditCardIcon />;
    case 'investment': return <InvestmentIcon />;
    default: return <BankIcon />;
  }
}

function ChangeArrow({ direction }) {
  if (direction === 'up') {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <polyline points="18,15 12,9 6,15" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="6,9 12,15 18,9" />
    </svg>
  );
}


/* ---- Main Page Component ---- */
export default function AccountsNetWorth() {
  const { data: accounts, isLoading, mutate: mutateAccounts } = useGetAccountSummaries();

  // Inline account nickname editing. The display name is computed on the server
  // from the account's fields; a nickname (when set) overrides it for display.
  const { data: isSuperAdmin } = useIsSuperAdmin();
  const [identityAccount, setIdentityAccount] = useState(null);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [titleAccount, setTitleAccount] = useState(null);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [editingNicknameId, setEditingNicknameId] = useState(null);
  const [nicknameDraft, setNicknameDraft] = useState('');
  const [savingNickname, setSavingNickname] = useState(false);

  const startEditNickname = (account) => {
    setEditingNicknameId(account.id);
    setNicknameDraft(account._nickname || '');
  };
  const cancelEditNickname = () => {
    setEditingNicknameId(null);
    setNicknameDraft('');
  };
  // `nickname` is passed explicitly rather than read from state so the reset
  // control can clear it without waiting for a setNicknameDraft re-render.
  const saveNickname = async (accountId, nickname = null) => {
    if (savingNickname) return;
    const value = (nickname === null ? nicknameDraft : nickname).trim();
    setSavingNickname(true);
    try {
      // Empty string clears the nickname (server falls back to the computed name).
      await updateAccount(accountId, { _nickname: value });
      await mutateAccounts();
      cancelEditNickname();
    } catch (e) {
      console.error('Failed to save account nickname', e);
    } finally {
      setSavingNickname(false);
    }
  };
  // Drop the override and go back to the server-computed name.
  const resetNickname = (accountId) => saveNickname(accountId, '');
  const { data: caseStats } = useGetCaseStats();

  const { data: rawTxnData, isLoading: isTxnLoading } = useGetEnrichedTransactions();
  const enrichedTransactions = useMemo(() => flattenInfiniteData(rawTxnData), [rawTxnData]);
  const [timeRange] = useState('ALL');
  const [netWorthView, setNetWorthView] = useState('performance');
  const [sankeyPerspective, setSankeyPerspective] = useState('CATEGORY');
  const keyDates = caseStats?.key_dates || [];
  const [showKeyDates, setShowKeyDates] = useState(true);
  const [expandedAccounts, setExpandedAccounts] = useState({});

  const toggleAccount = (id) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };


  // Group accounts by type (drives the summary panel and overall totals)
  const grouped = useMemo(() => {
    if (!accounts || !Array.isArray(accounts)) return {};
    return groupByType(accounts);
  }, [accounts]);

  // Compute net worth over time from all accounts' balance histories.
  // Each account only reports balances on its own statement dates, so we
  // carry forward the last-known balance per account across the union of
  // all dates — otherwise the last point would only include whichever
  // account happened to report on that date (usually just one side of the
  // ledger, which misrepresents the sign).
  const netWorthData = useMemo(() => {
    if (!accounts || !Array.isArray(accounts)) return null;

    // Build per-account sorted history. Backend already applies the expense
    // factor to balance_history (e.g. credit cards come back negative), so we
    // use point.balance directly — do NOT re-sign based on liability type.
    const perAccount = accounts
      .filter((a) => a.balance_history && a.balance_history.length > 0)
      .map((a) => {
        const sortedHistory = [...a.balance_history]
          .filter((p) => p.date)
          .sort((x, y) => x.date.localeCompare(y.date))
          .map((p) => ({ date: p.date, value: p.balance ?? 0 }));
        return sortedHistory;
      })
      .filter((h) => h.length > 0);

    if (perAccount.length === 0) return null;

    // Union of all dates, sorted
    const allDates = Array.from(
      new Set(perAccount.flatMap((h) => h.map((p) => p.date)))
    ).sort((a, b) => a.localeCompare(b));

    // For each date, sum carry-forward balances from every account
    const result = [];
    for (const date of allDates) {
      let total = 0;
      let anyActive = false;
      for (const history of perAccount) {
        // Find last known balance at or before this date
        let last = null;
        for (const point of history) {
          if (point.date <= date) last = point;
          else break;
        }
        if (last !== null) {
          total += last.value;
          anyActive = true;
        }
      }
      if (anyActive) result.push({ date, balance: total });
    }

    return result.length >= 2 ? result : null;
  }, [accounts]);

  // Compute totals
  const totals = useMemo(() => {
    let assets = 0;
    let liabilities = 0;

    for (const group of Object.values(grouped)) {
      if (group.isLiability) {
        liabilities += Math.abs(group.total);
      } else {
        assets += group.total;
      }
    }

    return {
      assets,
      liabilities,
      netWorth: assets - liabilities,
    };
  }, [grouped]);

  // Net worth change
  const netWorthChange = useMemo(() => {
    if (!netWorthData || netWorthData.length < 2) return null;
    const current = netWorthData[netWorthData.length - 1].balance;
    const previous = netWorthData[netWorthData.length - 2].balance;
    const change = current - previous;
    const pct = previous !== 0 ? (change / Math.abs(previous)) * 100 : null;
    return { change, pct };
  }, [netWorthData]);

  // Order groups: cash, credit, loan, investment, other (only show groups that
  // have accounts). Computed above the early returns below (hooks -- including
  // the PDF hook, which itself calls hooks -- can't follow a conditional return).
  const visibleGroups = GROUP_ORDER.filter((key) => grouped[key]?.accounts.length > 0);

  // Account list sections: Personal, then one per business / other entity,
  // each with its own type buckets and net subtotal. A single unlabeled
  // section when every account is personal, so that case looks as before.
  const listSections = useMemo(() => {
    if (!accounts || !Array.isArray(accounts) || !hasEntityAssignments(accounts)) {
      return [{ assignment: null, grouped, net: null,
        visibleGroups: GROUP_ORDER.filter((key) => grouped[key]?.accounts.length > 0) }];
    }
    return groupByAssignment(accounts).map(({ assignment, items }) => {
      const sectionGrouped = groupByType(items);
      return {
        assignment,
        grouped: sectionGrouped,
        visibleGroups: GROUP_ORDER.filter((key) => sectionGrouped[key]?.accounts.length > 0),
        net: netOfGroups(sectionGrouped),
      };
    });
  }, [accounts, grouped]);
  const firstEntitySectionIndex = listSections.findIndex(
    (s) => s.assignment && s.assignment.key !== PERSONAL_ASSIGNMENT_KEY);

  const handlePrint = usePrintAccountsNetWorthPDF({ accounts, visibleGroups, grouped, totals, netWorthData, listSections });

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Accounts & Net Worth" subtitle="Loading account data..." />
        <div className={styles.loadingState}>Loading...</div>
      </div>
    );
  }

  // Empty state
  if (!accounts || accounts.length === 0) {
    return (
      <div className={styles.page}>
        <PageHeader title="Accounts & Net Worth" subtitle="View all accounts and track net worth over time." />
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" /><path d="M3 10h18" /><path d="M12 3l9 7H3z" />
            </svg>
          </div>
          <div className={styles.emptyStateTitle}>No Bank Accounts Found</div>
          <div className={styles.emptyStateHint}>
            Upload bank statements to see your accounts and track net worth over time.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Accounts & Net Worth"
        subtitle="Track account balances and overall net worth across all bank accounts."
        actions={(
          <>
            <button type="button" onClick={() => setReorderOpen(true)} className={styles.printButton}>
              <SwapVertIcon fontSize="small" />
              Reorder Accounts
            </button>
            <button type="button" onClick={handlePrint} className={styles.printButton}>
              <PictureAsPdfIcon fontSize="small" />
              Print / Save as PDF
            </button>
          </>
        )}
      />

      {/* Net Worth Trend Card */}
      <div className={styles.netWorthCard}>
        <div className={styles.netWorthHeader}>
          <div className={styles.netWorthLabel}>Net Worth</div>
          <div className={styles.netWorthRow}>
            <span className={styles.netWorthValue}>{formatDollar(totals.netWorth)}</span>
            {netWorthChange && (
              <>
                <span className={`${styles.netWorthChange} ${netWorthChange.change >= 0 ? styles.up : styles.down}`}>
                  <ChangeArrow direction={netWorthChange.change >= 0 ? 'up' : 'down'} />
                  {formatChangeStr(netWorthChange.change, netWorthChange.pct)}
                </span>
                <span className={styles.netWorthPeriod}>1 period change</span>
              </>
            )}
          </div>
        </div>
        <div className={styles.netWorthControls}>
          <div className={styles.controlTabs} role="tablist" aria-label="Net worth chart view">
            <button
              role="tab"
              aria-selected={netWorthView === 'performance'}
              className={`${styles.controlTab} ${netWorthView === 'performance' ? styles.controlTabActive : ''}`}
              onClick={() => setNetWorthView('performance')}
            >
              Net worth performance
            </button>
            <button
              role="tab"
              aria-selected={netWorthView === 'cashflow'}
              className={`${styles.controlTab} ${netWorthView === 'cashflow' ? styles.controlTabActive : ''}`}
              onClick={() => setNetWorthView('cashflow')}
            >
              Cash Flow
            </button>
          </div>
          <div className={styles.controlTabs}>
            {netWorthView === 'performance' && keyDates.length > 0 && (
              <button
                className={`${styles.controlTab} ${showKeyDates ? styles.controlTabActive : ''}`}
                onClick={() => setShowKeyDates((v) => !v)}
              >
                Key Dates
              </button>
            )}
            {['ALL'].map((range) => (
              <button
                key={range}
                className={`${styles.controlTab} ${timeRange === range ? styles.controlTabActive : ''}`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.chartArea}>
          {netWorthView === 'performance' && netWorthData && (
            <NetWorthChart dataPoints={netWorthData} id="anw" keyDates={showKeyDates ? keyDates : undefined} />
          )}
          {netWorthView === 'cashflow' && (
            isTxnLoading
              ? <div className={styles.chartEmptyState}>Loading transactions…</div>
              : (enrichedTransactions && enrichedTransactions.length > 0
                ? <CashFlowChart transactions={enrichedTransactions} />
                : <div className={styles.chartEmptyState}>No transactions available to chart cash flow.</div>)
          )}
        </div>
      </div>

      {/* Accounts + Summary Grid */}
      <SectionTitle>Accounts</SectionTitle>
      <div className={styles.accountsSection}>
        {/* Left: Account Groups */}
        <div className={styles.accountsList}>
          {listSections.map((section, sectionIndex) => (
          <section
            key={section.assignment?.key || 'all'}
            className={styles.assignmentSection}
            aria-label={section.assignment ? `${section.assignment.label} accounts` : undefined}
          >
          {sectionIndex === firstEntitySectionIndex && (
            <div className={styles.entitiesHeader}>{ENTITY_ASSIGNMENTS_LABEL}</div>
          )}
          {section.assignment && (
            <div className={styles.assignmentHeader}>
              <span className={styles.assignmentName}>
                {section.assignment.label}
                {section.assignment.kind && (
                  <span className={styles.assignmentKind}>{section.assignment.kind}</span>
                )}
              </span>
              <span className={styles.assignmentNet}>Net {formatDollar(section.net)}</span>
            </div>
          )}
          {section.visibleGroups.map((groupKey) => {
            const group = section.grouped[groupKey];
            const changeDir = group.totalChange >= 0 ? 'up' : 'down';

            return (
              <div key={groupKey} className={styles.accountGroup}>
                <div
                  className={styles.accountGroupHeader}
                  role="button"
                  tabIndex={0}
                  aria-label={`${group.label} group, total ${formatDollar(group.total)}`}
                >
                  <div className={styles.accountGroupLeft}>
                    <span className={styles.accountGroupName}>{group.label}</span>
                    {group.totalChange !== 0 && (
                      <span className={`${styles.accountGroupChange} ${styles[changeDir]}`}>
                        {formatChangeStr(group.totalChange)}
                      </span>
                    )}
                  </div>
                  <span className={styles.accountGroupTotal}>{formatDollar(group.total)}</span>
                </div>
                {group.accounts.map((account) => {
                  const hasHoldings = account.holdings && account.holdings.length > 0;
                  const hasMergedNumbers = account.merged_account_numbers && account.merged_account_numbers.length > 0;
                  // One caret per row toggles whatever detail the account has --
                  // portfolio holdings and/or retained old account numbers.
                  const isExpandable = hasHoldings || hasMergedNumbers;
                  const isExpanded = !!expandedAccounts[account.id];
                  const isEditingNickname = editingNicknameId === account.id;

                  return (
                    <div key={account.id} style={{ borderBottom: 'var(--border-light)' }}>
                      <div
                        className={styles.accountRow}
                        onClick={isExpandable ? () => toggleAccount(account.id) : undefined}
                        role={isExpandable ? 'button' : undefined}
                        tabIndex={isExpandable ? 0 : undefined}
                        aria-expanded={isExpandable ? isExpanded : undefined}
                        aria-label={`${account._displayName || account.bank_name || 'Account'}, balance ${formatDollar(account.latest_balance)}${isExpandable ? ', click to toggle details' : ''}`}
                        onKeyDown={isExpandable ? (e) => {
                          // Only a keypress on the row itself toggles. Anything bubbling up from a
                          // descendant belongs to that descendant: Space is a character in the
                          // nickname input and the activation key on the rename/identity/merge buttons.
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleAccount(account.id);
                          }
                        } : undefined}
                        style={{ cursor: isExpandable ? 'pointer' : 'default' }}
                      >
                        <div className={`${styles.accountIcon} ${styles[group.iconClass]}`}>
                          {getAccountIcon(groupKey)}
                        </div>
                        <div className={`${styles.accountInfo}${isEditingNickname ? ` ${styles.accountInfoEditing}` : ''}`}>
                          <div className={styles.accountName}>
                            {isEditingNickname ? (
                              <span className={styles.nicknameEditRow}>
                              <input
                                className={styles.nicknameInput}
                                value={nicknameDraft}
                                autoFocus
                                disabled={savingNickname}
                                maxLength={120}
                                placeholder={account._displayName || 'Account name'}
                                aria-label="Account nickname"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setNicknameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); saveNickname(account.id); }
                                  else if (e.key === 'Escape') { e.preventDefault(); cancelEditNickname(); }
                                }}
                                // Clicking away discards, like Escape -- Enter is the only commit.
                                // A stray click should not rename an account, and blur fires for any
                                // click on the row (which also toggles holdings). saveNickname's own
                                // blur, fired as the field unmounts on save, must not cancel first.
                                onBlur={() => { if (!savingNickname) cancelEditNickname(); }}
                              />
                              {account._nickname ? (
                                <button
                                  type="button"
                                  className={styles.nicknameResetButton}
                                  // Without this the input blurs first and onBlur cancels the
                                  // edit, unmounting the button before its click ever lands.
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => { e.stopPropagation(); resetNickname(account.id); }}
                                  disabled={savingNickname}
                                  title="Discard the custom name and go back to the account's original name"
                                >
                                  Reset to original
                                </button>
                              ) : null}
                              </span>
                            ) : (
                              <span className={styles.accountNameRow}>
                                <span className={styles.accountNameText}>
                                  {account._displayName || account.bank_name || `Account ...${account.account_number_last4 || ''}`}
                                </span>
                              </span>
                            )}
                          </div>
                          <div className={styles.accountType}>
                            {account.account_type_label
                              || formatAccountType(account.account_type)
                              || 'Account'}
                          </div>
                          <button
                            type="button"
                            className={styles.accountTitleButton}
                            onClick={(e) => { e.stopPropagation(); setTitleAccount(account); }}
                            aria-label={`Edit title for ${account._displayName || 'account'}`}
                            title="Whose name is on this account"
                          >
                            {formatOwnershipAllocation(account.title, { abbreviate: true }) || 'Untitled'}
                          </button>
                          <button
                            type="button"
                            className={styles.accountTitleButton}
                            onClick={(e) => { e.stopPropagation(); setLedgerAccount(account); }}
                            aria-label={`Personal or business: ${account._displayName || 'account'} is ${accountLedgerLabel(account)}`}
                            title="Personal or business account"
                          >
                            {accountLedgerLabel(account)}
                          </button>
                        </div>
                        {/* A dedicated column, not part of accountInfo/accountNameRow -- these
                            only reveal on row hover (opacity 0->1), but a fixed-width column
                            of their own means they never compete with, or overlay, the name
                            text for space regardless of how long the name is. */}
                        <div className={styles.accountActions}>
                          <button
                            type="button"
                            className={styles.nicknameEditButton}
                            aria-label={`Rename ${account._displayName || 'account'}`}
                            onClick={(e) => { e.stopPropagation(); startEditNickname(account); }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M12 20h9" />
                              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                            </svg>
                          </button>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className={styles.nicknameEditButton}
                              aria-label={`Correct identity for ${account._displayName || 'account'}`}
                              title="Correct account identity (super-admin)"
                              onClick={(e) => { e.stopPropagation(); setIdentityAccount(account); }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
                              </svg>
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              type="button"
                              className={styles.nicknameEditButton}
                              aria-label={`Merge ${account._displayName || 'account'} into another account`}
                              title="Merge into another account (super-admin)"
                              onClick={(e) => { e.stopPropagation(); setMergeCandidate(account); }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M6 3v12" />
                                <path d="M18 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
                                <path d="M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                                <path d="M15 6a9 9 0 0 1-9 9" />
                              </svg>
                            </button>
                          )}
                        </div>
                        <div className={styles.accountSparkline}>
                          <Sparkline
                            balanceHistory={account.balance_history}
                            color={group.sparkColor}
                          />
                        </div>
                        <div className={styles.accountBalanceCol}>
                          <div className={styles.accountBalance}>{formatDollar(account.latest_balance)}</div>
                          {account.balance_change != null && account.balance_change !== 0 && (
                            <div className={styles.accountBalanceLabel}>
                              {account.balance_change >= 0 ? '+' : ''}{formatDollar(account.balance_change)}
                            </div>
                          )}
                        </div>
                        {isExpandable && (
                          <div className={styles.holdingsExpandCaret}>
                            {isExpanded ? '▼' : '▶'}
                          </div>
                        )}
                      </div>

                      {/* Expanded holdings section */}
                      {isExpanded && hasHoldings && (
                        <div className={styles.holdingsContainer}>
                          <div className={styles.holdingsHeader}>
                            <div className={styles.holdingsTitle}>Portfolio Holdings</div>
                            {account.holdings[0]?.as_of_date && (
                              <div className={styles.holdingsAsOf}>
                                As of {account.holdings[0].as_of_date}
                              </div>
                            )}
                          </div>
                          <table className={styles.holdingsTable}>
                            <thead>
                              <tr>
                                <th>Asset / Security</th>
                                <th>Symbol</th>
                                <th className={styles.holdingsThRight}>Shares / Qty</th>
                                <th className={styles.holdingsThRight}>Total Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {account.holdings.map((holding, idx) => {
                                const isCash = holding.type ? holding.type === 'CashPosition' : !holding.ticker;
                                return (
                                  <tr key={holding.id || idx}>
                                    <td>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <span className={styles.holdingsText}>{holding.security_name || 'Cash Sweep'}</span>
                                        <span className={`${styles.holdingsTypeBadge} ${isCash ? styles.holdingsTypeCash : styles.holdingsTypeSecurity}`}>
                                          {isCash ? 'Cash' : 'Security'}
                                        </span>
                                      </div>
                                    </td>
                                    <td>{holding.ticker || '—'}</td>
                                    <td className={styles.holdingsValue}>
                                      {isCash ? '—' : (holding.quantity != null ? formatQuantity(holding.quantity) : '—')}
                                    </td>
                                    <td className={`${styles.holdingsValue} ${styles.holdingsValueStrong}`}>
                                      {isCash 
                                        ? (holding.quantity != null ? formatDollar(holding.quantity) : '—') 
                                        : (holding.total_market_value != null ? formatDollar(holding.total_market_value) : '—')}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Expanded old-account-numbers section (from account-number-change merges) */}
                      {isExpanded && hasMergedNumbers && (
                        <div className={styles.holdingsContainer}>
                          <div className={styles.holdingsHeader}>
                            <div className={styles.holdingsTitle}>Previous Account Numbers</div>
                          </div>
                          <div className={styles.mergedNumbersList}>
                            {account.merged_account_numbers.map((merged, idx) => (
                              <span key={merged.id || idx} className={styles.mergedNumberBadge}>
                                {merged._displayName || `*${merged.account_number_last4 || '????'}`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

              </div>
            );
          })}
          </section>
          ))}
        </div>

        {/* Right: Summary Panel */}
        <div className={styles.summaryCard}>
          <div className={styles.summaryTitle}>Summary</div>

          {/* Assets */}
          <div className={styles.summarySection}>
            <div className={styles.summarySectionHeader}>
              <span className={styles.summarySectionLabel}>Assets</span>
              <span className={styles.summarySectionTotal}>{formatDollar(totals.assets)}</span>
            </div>
            {visibleGroups
              .filter((key) => !ACCOUNT_GROUPS[key].isLiability)
              .map((groupKey) => (
                <div key={groupKey} className={styles.summaryRow}>
                  <span className={styles.summaryRowLabel}>
                    <span
                      className={styles.summaryRowDot}
                      style={{ background: ACCOUNT_GROUPS[groupKey].dotColor }}
                    />
                    {ACCOUNT_GROUPS[groupKey].label}
                  </span>
                  <span className={styles.summaryRowValue}>{formatDollar(grouped[groupKey].total)}</span>
                </div>
              ))}
          </div>

          {/* Liabilities */}
          {visibleGroups.some((key) => ACCOUNT_GROUPS[key].isLiability) && (
            <div className={styles.summarySection}>
              <div className={styles.summarySectionHeader}>
                <span className={styles.summarySectionLabel}>Liabilities</span>
                <span className={styles.summarySectionTotal}>{formatDollar(totals.liabilities)}</span>
              </div>
              {visibleGroups
                .filter((key) => ACCOUNT_GROUPS[key].isLiability)
                .map((groupKey) => (
                  <div key={groupKey} className={styles.summaryRow}>
                    <span className={styles.summaryRowLabel}>
                      <span
                        className={styles.summaryRowDot}
                        style={{ background: ACCOUNT_GROUPS[groupKey].dotColor }}
                      />
                      {ACCOUNT_GROUPS[groupKey].label}
                    </span>
                    <span className={styles.summaryRowValue}>{formatDollar(Math.abs(grouped[groupKey].total))}</span>
                  </div>
                ))}
            </div>
          )}

          {/* Net Worth */}
          <div className={styles.summaryDivider} />
          <div className={styles.summaryNetWorth}>
            <span className={styles.summaryNetLabel}>Net Worth</span>
            <span className={totals.netWorth >= 0 ? styles.summaryNetValuePositive : styles.summaryNetValueNegative}>
              {formatDollar(totals.netWorth)}
            </span>
          </div>
        </div>
      </div>

      {/* Money Flow Sankey */}
      <SectionTitle
        actions={
          <div className={styles.sankeyTabs} role="tablist" aria-label="Money flow perspective">
            {Object.entries(perspectiveMap).map(([key, meta]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={sankeyPerspective === key}
                onClick={() => setSankeyPerspective(key)}
                className={`${styles.sankeyTab} ${sankeyPerspective === key ? styles.sankeyTabActive : ''}`}
              >
                {meta.label}
              </button>
            ))}
          </div>
        }
      >
        Money Flow
      </SectionTitle>
      <div className={styles.sankeyCard}>
        {isTxnLoading && (
          <div className={styles.sankeyEmpty}>Loading transactions…</div>
        )}
        {!isTxnLoading && (!enrichedTransactions || enrichedTransactions.length === 0) && (
          <div className={styles.sankeyEmpty}>No transactions available to visualize money flow.</div>
        )}
        {!isTxnLoading && enrichedTransactions && enrichedTransactions.length > 0 && (
          <ReportCategorySankeyAnalysis
            data={enrichedTransactions}
            isLoading={isTxnLoading}
            enabledTiers={tiersForPerspective(sankeyPerspective)}
            displayOptions={{ maxNodesPerType: 10 }}
          />
        )}
      </div>
      {isSuperAdmin && (
        <AccountIdentityCorrectionDialog
          open={!!identityAccount}
          account={identityAccount}
          onClose={() => setIdentityAccount(null)}
          onSaved={mutateAccounts}
        />
      )}
      {isSuperAdmin && (
        <AccountMergeDialog
          open={!!mergeCandidate}
          account={mergeCandidate}
          candidates={accounts}
          onClose={() => setMergeCandidate(null)}
          onSaved={mutateAccounts}
        />
      )}
      <AccountLedgerDialog
        open={!!ledgerAccount}
        account={ledgerAccount}
        onClose={() => setLedgerAccount(null)}
        onSaved={mutateAccounts}
      />
      <AccountTitleEditDialog
        open={!!titleAccount}
        account={titleAccount}
        cacheKey={ACCOUNT_SUMMARIES_CACHE_KEY}
        onClose={() => setTitleAccount(null)}
        onSaved={mutateAccounts}
      />
      <ReorderAccountsDialog
        open={reorderOpen}
        accounts={accounts}
        onClose={() => setReorderOpen(false)}
        onSaved={mutateAccounts}
      />
    </div>
  );
}
